import { loadStylesWithRetry } from './tools'

//@ts-expect-error: We know we're a script
const hostOrigin = new URL(document.currentScript?.src).origin

export type TOnConnection = (
  root: ShadowRoot,
  attributes: Record<string, string>,
  host: string,
  onReady: () => void
) => void

export type TOnDisconnection<T> = (node: TRootNode<T> | null) => void

export type TRootNode<T> = ShadowRoot & { applicationNode?: T }

export function createWebComponent<T>(
  name: string,
  onConnection: TOnConnection,
  onDisconnection: TOnDisconnection<T>
) {
  customElements.define(
    name,
    class extends HTMLElement {
      mounted: boolean
      host: ShadowRoot | null

      constructor() {
        super()
        this.mounted = false
        this.host = null
      }

      connectedCallback() {
        const attributes = this.getAttributeNames().reduce(
          (obj, qualifiedName) => ({
            ...obj,
            [qualifiedName]: this.getAttribute(qualifiedName)
          }),
          {}
        )

        if (!this.mounted) {
          this.mounted = true
          this.host = this.attachShadow({ mode: 'open' })

          onConnection(
            this.host,
            attributes,
            hostOrigin,
            () => this.dispatchEvent(new Event('micro-frontend-attached')) // https://casumo.atlassian.net/browse/PBOB-295
          )
        }
      }

      disconnectedCallback() {
        this.mounted = false
        onDisconnection(this.host)
      }
    }
  )

  customElements
    .whenDefined(name)
    .then(() => {
      const el = document.querySelector(name)
      // if there is no element then script has run before `fabric-game-list` was rendered, nothing to do
      if (el) {
        customElements.upgrade(el)
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error(`Error when upgrading web component ${name}`, err)
    })
}

export function createStylesLink(node: ShadowRoot, href: string) {
  return loadStylesWithRetry(node, href)
}

export async function createApplicationContainer(node: ShadowRoot) {
  const container = document.createElement('main')

  node.appendChild(container)

  return container
}
